import { FC, Fragment, useMemo, useState } from "react";
import { Alert, Col, Form, Modal, Row } from "react-bootstrap";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSnapshot } from "valtio";
import GenericForm from "../GenericForm.tsx";
import GenericInput from "../../../../components/form/GenericInput.tsx";
import GenericMaskInput from "../../../../components/form/GenericMaskInput.tsx";
import GenericButton from "../../../../components/form/GenericButton.tsx";
import { EngagementService } from "@services/engagements/engagementService.ts";
import { PersonService } from "@services/person/PersonService.ts";
import { TeamMember, TeamRole } from "@models/teamMember.model.ts";
import { ChoiceEnum } from "@models/choiceEnum.ts";
import { Employee } from "@models/employee.model.ts";
import { MediaFile } from "@models/mediaFile.model.ts";
import { teamMemberStore } from "./teamMemberStore.ts";
import { engagementStore } from "../../engagementStore.ts";

/** What the modal was opened to do. `null` means closed. */
export type MemberDialog =
  | { mode: "add"; side: "CLIENT" | "PROVIDER" }
  | { mode: "edit"; member: TeamMember };

interface FormValues {
  employee_id: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  /** Existing media, a freshly picked file, or null when cleared. */
  profilePicture: MediaFile | File | null;
  /** Every role the person is to hold. Saving sends the whole set. */
  roles: ChoiceEnum[];
}

const EMPTY_VALUES: FormValues = {
  employee_id: -1,
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  profilePicture: null,
  roles: [],
};

function valuesFromMember(member: TeamMember): FormValues {
  return {
    employee_id: member.employee.id,
    firstName: member.employee.person.first_name,
    lastName: member.employee.person.last_name,
    phone: member.employee.person.phone,
    email: member.employee.person.email,
    profilePicture: member.employee.person.profile_picture,
    // Implied roles are left out: they have no row to keep, and sending one
    // back would try to store what the Organization role already grants.
    roles: member.roles.filter((role) => !role.is_implied),
  };
}

const schema = Yup.object().shape({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string().required("Email is required").email(),
});

interface Props {
  dialog: MemberDialog | null;
  onClose: () => void;
  onSaved: () => void;
}

/*
    Add or edit one engagement team member.

    Which side (client / internal) is decided by the caller — either the button
    that opened this, or the member being edited — so there is no member-type
    step. Formik owns the form values; there is no parallel copy of them.
*/
const TeamMemberModal: FC<Props> = ({ dialog, onClose, onSaved }) => {
  const { clientRoles, providerRoles, employees, clientEmployees } = useSnapshot(teamMemberStore);
  const { selectedEngagement } = useSnapshot(engagementStore);

  // Only client members can be created from scratch; internal members are
  // always picked from existing provider contacts.
  const [source, setSource] = useState<"NEW" | "EXISTING">("EXISTING");
  const [pictureCleared, setPictureCleared] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isEdit = dialog?.mode === "edit";
  const side = !dialog
    ? "CLIENT"
    : dialog.mode === "add"
      ? dialog.side
      : dialog.member.employee.organization.id === selectedEngagement.client.id
        ? "CLIENT"
        : "PROVIDER";

  const roles = (side === "CLIENT" ? clientRoles : providerRoles) as ChoiceEnum[];
  const contacts = (side === "CLIENT" ? clientEmployees : employees) as Employee[];
  const impliedRoles =
    dialog?.mode === "edit" ? dialog.member.roles.filter((role) => role.is_implied) : [];

  // Editing always shows the person's details; adding shows them only when
  // creating a brand new contact.
  const showPersonFields = isEdit || source === "NEW";

  const initialValues = useMemo(
    () => (dialog?.mode === "edit" ? valuesFromMember(dialog.member) : EMPTY_VALUES),
    [dialog],
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values: FormValues) => {
      const service = new EngagementService();
      const personService = new PersonService();

      const request = new TeamMember();
      request.employee.id = values.employee_id;
      request.employee.person.first_name = values.firstName;
      request.employee.person.last_name = values.lastName;
      request.employee.person.email = values.email;
      request.employee.person.phone = values.phone;
      request.roles = values.roles.map((role) => {
        const teamRole = new TeamRole();
        teamRole.value = role.value;
        teamRole.label = role.label;
        return teamRole;
      });

      setSubmitError(null);
      try {
        const saved = await service.createTeamMember(selectedEngagement.id, request);
        const personId = saved.employee.person.id;

        if (values.profilePicture instanceof File) {
          await personService.addProfilePicture(personId, values.profilePicture);
        } else if (pictureCleared) {
          await personService.deleteProfilePicture(personId);
        }

        close();
        onSaved();
      } catch (error) {
        setSubmitError(error instanceof Error ? error.message : "Failed to save team member");
      }
    },
  });

  const close = () => {
    setSource("EXISTING");
    setPictureCleared(false);
    setSubmitError(null);
    formik.resetForm();
    onClose();
  };

  const selectContact = (employeeId: number) => {
    const employee = contacts.find((e) => e.id === employeeId);
    if (!employee) return;
    formik.setValues({
      ...formik.values,
      employee_id: employee.id,
      firstName: employee.person.first_name,
      lastName: employee.person.last_name,
      email: employee.person.email,
      phone: employee.person.phone,
      profilePicture: employee.person.profile_picture,
    });
    setPictureCleared(false);
  };

  const clearPicture = () => {
    formik.setFieldValue("profilePicture", null);
    setPictureCleared(true);
  };

  const pickPicture = (file: File) => {
    formik.setFieldValue("profilePicture", file);
    setPictureCleared(false);
  };

  const picture = formik.values.profilePicture;
  const pictureUrl =
    picture instanceof File
      ? URL.createObjectURL(picture)
      : (picture?.storage_uri ?? null);

  // A member with no role is what the server refuses, so the button says so
  // first. Removing someone entirely is the card's delete, not an empty save.
  const canSubmit =
    (showPersonFields || formik.values.employee_id !== -1) &&
    formik.values.roles.length > 0;
  const sideLabel = side === "CLIENT" ? "client" : "internal";

  return (
    <Modal show={dialog !== null} onHide={close} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">
          {isEdit ? "Edit" : "Add"} {sideLabel} team member
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <GenericForm formik={formik} handleSubmit={formik.handleSubmit}>
          <Fragment>
            {!isEdit && side === "CLIENT" && (
              <div className="mb-3">
                <Form.Label className="mb-2 fw-normal">Contact</Form.Label>
                <div className="btn-group d-flex" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${source === "EXISTING" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setSource("EXISTING")}
                  >
                    Existing contact
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${source === "NEW" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setSource("NEW");
                      formik.setValues({ ...EMPTY_VALUES, roles: formik.values.roles });
                      setPictureCleared(false);
                    }}
                  >
                    New contact
                  </button>
                </div>
              </div>
            )}

            <div className="mb-3">
              <Form.Label htmlFor="roles" className="mb-2 fw-normal">
                Team roles
              </Form.Label>
              <Select
                inputId="roles"
                isMulti
                options={roles.map((role) => ({ value: role.value, label: role.label }))}
                value={formik.values.roles.map((role: ChoiceEnum) => ({
                  value: role.value,
                  label: role.label,
                }))}
                onChange={(options: any) =>
                  formik.setFieldValue(
                    "roles",
                    (options ?? []).map((option: any) =>
                      roles.find((role) => role.value === option.value),
                    ).filter(Boolean),
                  )
                }
                menuPlacement="auto"
                classNamePrefix="Select2"
                placeholder="Select one or more team roles..."
              />
              <Form.Text className="text-muted">
                Someone can hold several roles, and they get what all of them allow.
              </Form.Text>
              {impliedRoles.length > 0 && (
                <div className="mt-2">
                  {impliedRoles.map((role) => (
                    <span key={role.value} className="team-role team-role--implied me-1">
                      {role.label}
                    </span>
                  ))}
                  <Form.Text className="text-muted d-block">
                    Held through an Organization role. Change that role to remove it.
                  </Form.Text>
                </div>
              )}
            </div>

            {!isEdit && source === "EXISTING" && (
              <div className="mb-3">
                <Form.Label className="mb-2 fw-normal">
                  Choose a {sideLabel} contact
                </Form.Label>
                <Select
                  onChange={(option: any) => option && selectContact(Number(option.value))}
                  options={contacts.map((employee) => ({
                    value: employee.id,
                    label: `${employee.person.first_name} ${employee.person.last_name}`,
                  }))}
                  value={
                    formik.values.employee_id !== -1
                      ? {
                          value: formik.values.employee_id,
                          label: `${formik.values.firstName} ${formik.values.lastName}`,
                        }
                      : null
                  }
                  menuPlacement="auto"
                  classNamePrefix="Select2"
                  placeholder="Search and select..."
                />
              </div>
            )}

            {showPersonFields && (
              <>
                <div className="d-flex align-items-center gap-3 mb-3">
                  {pictureUrl ? (
                    <>
                      <img
                        src={pictureUrl}
                        className="rounded-circle"
                        style={{ width: 72, height: 72, objectFit: "cover" }}
                        alt="Profile"
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={clearPicture}
                      >
                        <i className="bi bi-trash me-1" />
                        Remove photo
                      </button>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-circle text-muted" style={{ fontSize: "3rem" }} />
                      <div>
                        <label htmlFor="profilePictureUpload" className="btn btn-sm btn-outline-primary mb-1">
                          <i className="bi bi-upload me-1" />
                          Upload photo
                        </label>
                        <input
                          id="profilePictureUpload"
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) pickPicture(file);
                          }}
                        />
                        <p className="text-muted mb-0" style={{ fontSize: "0.75rem" }}>
                          JPG, PNG or GIF (max. 5MB)
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <Row>
                  <Col xs={12} md={6} className="mb-3">
                    <GenericInput
                      disabled={false}
                      type="text"
                      name="firstName"
                      label="First Name"
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col xs={12} md={6} className="mb-3">
                    <GenericInput
                      disabled={false}
                      type="text"
                      name="lastName"
                      label="Last Name"
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col xs={12} className="mb-3">
                    <GenericInput
                      disabled={false}
                      type="email"
                      name="email"
                      label="Email Address"
                      onChange={formik.handleChange}
                    />
                  </Col>
                  <Col xs={12} className="mb-3">
                    <GenericMaskInput
                      mask="(999)-999-9999"
                      disabled={false}
                      type="text"
                      name="phone"
                      label="Phone Number"
                      formik={formik}
                    />
                  </Col>
                </Row>
              </>
            )}

            {submitError && (
              <Alert
                variant="danger"
                className="mt-3 mb-0"
                onClose={() => setSubmitError(null)}
                dismissible
              >
                {submitError}
              </Alert>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <GenericButton onClick={close} isPrimary={false} label="Cancel" type="button" />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSubmit || formik.isSubmitting}
              >
                {formik.isSubmitting ? "Saving..." : "Save member"}
              </button>
            </div>
          </Fragment>
        </GenericForm>
      </Modal.Body>
    </Modal>
  );
};

export default TeamMemberModal;
